import JSZip from "jszip";
import { getApplicationDocuments } from "../api/admin/applicationDocuments.js";
import { fetchFrappeFileBlob } from "./frappeFileDownload.js";

export const APPLICATION_STATIC_DOWNLOAD_DOCS = [
  {
    key: "eor",
    label: "EoR",
    full: "Evidence of Research & Illustration",
    filename: "EoR 5yr Fixed 07072025.pdf",
  },
  {
    key: "esis",
    label: "ESIS",
    full: "Illustration (ESIS)",
    filename: "ESIS Example v0.1 20251013 C&I.docx",
  },
];

function uniqueZipPath(basePath, usedPaths) {
  if (!usedPaths.has(basePath)) {
    usedPaths.add(basePath);
    return basePath;
  }

  const dot = basePath.lastIndexOf(".");
  const stem = dot > 0 ? basePath.slice(0, dot) : basePath;
  const ext = dot > 0 ? basePath.slice(dot) : "";
  let index = 2;
  let candidate = `${stem} (${index})${ext}`;

  while (usedPaths.has(candidate)) {
    index += 1;
    candidate = `${stem} (${index})${ext}`;
  }

  usedPaths.add(candidate);
  return candidate;
}

export function buildStaticDownloadDocuments() {
  return APPLICATION_STATIC_DOWNLOAD_DOCS.map((doc) => ({
    type: "static",
    zipPath: `illustrations/${doc.filename}`,
    filename: doc.filename,
    url: `/docs/${encodeURIComponent(doc.filename)}`,
  }));
}

export function mapBorrowerDocumentsToDownload(files = []) {
  const usedPaths = new Set();

  return files
    .filter((file) => file?.file_url)
    .map((file) => {
      const filename = file.file_name || file.name || "document";
      const field = file.attached_to_field || "uploads";
      const basePath = `borrower/${field}/${filename}`;

      return {
        type: "frappe",
        zipPath: uniqueZipPath(basePath, usedPaths),
        filename,
        fileUrl: file.file_url,
        fileId: file.name,
        isPrivate: file.is_private || String(file.file_url).startsWith("/private/"),
        attachedToField: file.attached_to_field,
        createdAt: file.creation,
      };
    });
}

export async function collectApplicationDocumentsForDownload(applicationId) {
  const staticDocs = buildStaticDownloadDocuments();

  let borrowerDocs = [];
  try {
    const files = await getApplicationDocuments(applicationId);
    borrowerDocs = mapBorrowerDocumentsToDownload(files);
  } catch (error) {
    console.warn("[download] Could not load borrower documents:", error);
  }

  return [...staticDocs, ...borrowerDocs];
}

async function fetchDocumentBlob(doc) {
  if (doc.type === "frappe") {
    return fetchFrappeFileBlob({
      fileUrl: doc.fileUrl,
      fileId: doc.fileId,
      isPrivate: doc.isPrivate,
    });
  }

  const response = await fetch(doc.url);
  if (!response.ok) {
    throw new Error(`Failed to fetch ${doc.filename}`);
  }
  return response.blob();
}

export async function downloadAllApplicationDocuments({ applicationId, documents }) {
  const docs =
    documents ||
    (applicationId ? await collectApplicationDocumentsForDownload(applicationId) : []);

  if (!docs.length) {
    throw new Error("No documents available to download");
  }

  const zip = new JSZip();
  const folderName = String(applicationId || "application").replace(/[^\w.-]+/g, "_");
  const usedPaths = new Set();

  const results = await Promise.allSettled(
    docs.map(async (doc) => {
      const filename = doc.filename || doc.name;
      if (!filename) throw new Error("Missing filename");

      const zipPath = uniqueZipPath(doc.zipPath || filename, usedPaths);
      const blob = await fetchDocumentBlob(doc);
      return { zipPath, blob };
    })
  );

  const successful = results.filter((result) => result.status === "fulfilled");
  if (!successful.length) {
    throw new Error("No documents could be downloaded");
  }

  successful.forEach((result) => {
    zip.file(result.value.zipPath, result.value.blob);
  });

  const zipBlob = await zip.generateAsync({ type: "blob" });
  const objectUrl = URL.createObjectURL(zipBlob);
  const link = document.createElement("a");
  link.href = objectUrl;
  link.download = `${folderName}-documents.zip`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(objectUrl);
}
